import React, { useEffect, useMemo, useRef } from 'react';
import QRCode from 'qrcode';
import SignatureProgressStepper from './SignatureProgressStepper';
import SignatureStatusCard from './SignatureStatusCard';
import { SIGNATURE_STATUS } from './signatureStatusConstants';

const canUseNativeShare = () => typeof navigator !== 'undefined' && typeof navigator.share === 'function';

const isValidSignatureUrl = (value = '') => {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch (error) {
    return false;
  }
};

const MobileSignatureLinkPanel = ({
  signatureLink = '',
  signatureStatus = SIGNATURE_STATUS.IDLE,
  signatureExpiresAt = '',
  lastSignatureStatusCheckedAt = '',
  signatureSignedAt = '',
  signatureApiMessage = '',
  signatureError = '',
  signatureActionMessage = '',
  signatureActionError = '',
  isGeneratingLink = false,
  isRegeneratingLink = false,
  isSharingLink = false,
  isRefreshingSignature = false,
  canRefreshSignatureStatus = undefined,
  onCopyLink,
  onShareLink,
  onRegenerateLink,
  onRefreshStatus
}) => {
  const isCompleted = signatureStatus === SIGNATURE_STATUS.COMPLETED;
  const isPending = signatureStatus === SIGNATURE_STATUS.PENDING;
  const isGenerating = signatureStatus === SIGNATURE_STATUS.GENERATING || isGeneratingLink;
  const isExpired = signatureStatus === SIGNATURE_STATUS.EXPIRED;
  const isSuperseded = signatureStatus === SIGNATURE_STATUS.SUPERSEDED;
  const isNotSignable = signatureStatus === SIGNATURE_STATUS.NOT_SIGNABLE;
  const isStatusError = signatureStatus === SIGNATURE_STATUS.ERROR;
  const isTerminalLinkState = isExpired || isSuperseded;
  const hasUsableLink = isValidSignatureUrl(signatureLink);
  const shareSupported = canUseNativeShare();
  const canUseCurrentLink = hasUsableLink && !isTerminalLinkState && !isNotSignable && !isCompleted;
  const showLinkContent = canUseCurrentLink && (!isGenerating || isRegeneratingLink);
  const showLinkActions = showLinkContent && !isStatusError;
  const canRefreshStatus = (canRefreshSignatureStatus ?? hasUsableLink) && hasUsableLink;
  const showRegenerateRecovery = isExpired || isSuperseded || isNotSignable;
  const regenerateButtonLabel = isRegeneratingLink ? 'Generando...' : 'Generar un enlace nuevo';

  const progressSteps = useMemo(() => {
    const signed = isCompleted;

    const resolveState = (done, active) => {
      if (done) return 'completed';
      if (active) return 'active';
      return 'pending';
    };

    return [
      {
        id: 'signed',
        index: 1,
        label: 'Firma del cliente',
        state: resolveState(signed, isPending && hasUsableLink && !isTerminalLinkState)
      },
      {
        id: 'confirm',
        index: 2,
        label: 'Validación de la firma',
        state: resolveState(signed, signed)
      }
    ];
  }, [hasUsableLink, isCompleted, isPending, isTerminalLinkState]);

  const qrCanvasRef = useRef(null);

  useEffect(() => {
    if (!showLinkContent || !qrCanvasRef.current) {
      return;
    }

    Promise.resolve(QRCode.toCanvas(qrCanvasRef.current, signatureLink, {
      width: 200,
      margin: 2,
      color: {
        dark: '#000000',
        light: '#ffffff'
      }
    })).catch(() => {
      // QR rendering failures should not block the rest of the panel.
    });
  }, [showLinkContent, signatureLink]);

  return (
    <section className="rg-signature-panel rg-signature-panel--digital" aria-label="Firma digital desde celular">
      <div className="rg-signature-panel__intro">
        <p className="rg-signature-panel__description">
          Compartí el enlace o el QR con el cliente. Cuando firme, vas a poder continuar.
        </p>
      </div>

      {showRegenerateRecovery && (
        <div className="rg-signature-panel__expired">
          {isExpired && <p className="rg-signature-panel__expired-title">Enlace vencido</p>}
          <p className="rg-signature-panel__expired-copy">
            {isSuperseded
              ? 'Este enlace fue reemplazado. Generá uno nuevo si necesitás compartir la firma nuevamente.'
              : isNotSignable
                ? 'Generá un enlace nuevo cuando la solicitud vuelva a estar disponible para firma.'
                : 'El enlace anterior ya no sirve. Generá uno nuevo para compartirlo con el cliente.'}
          </p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onRegenerateLink}
            disabled={isGeneratingLink}
            aria-busy={isRegeneratingLink}
          >
            {regenerateButtonLabel}
          </button>
        </div>
      )}

      <div
        className={`rg-signature-panel__digital-body${showLinkContent ? ' rg-signature-panel__digital-body--with-qr' : ''}`}
      >
        <div className="rg-signature-panel__digital-content">
          <SignatureStatusCard
            signatureStatus={isGenerating ? SIGNATURE_STATUS.GENERATING : signatureStatus}
            signatureExpiresAt={signatureExpiresAt}
            lastSignatureStatusCheckedAt={lastSignatureStatusCheckedAt}
            signatureSignedAt={signatureSignedAt}
            apiMessage={signatureApiMessage}
            isRefreshing={isRefreshingSignature}
            canRefresh={canRefreshStatus}
            onRefreshStatus={onRefreshStatus}
          />

          <SignatureProgressStepper steps={progressSteps} />

          {showLinkContent && (
            <>
              <div className="rg-signature-panel__link-field">
                <label className="form-label" htmlFor="signature-link-input">
                  Enlace de firma
                </label>
                <div className="rg-signature-panel__link-control">
                  <input
                    id="signature-link-input"
                    type="text"
                    className="form-control"
                    value={signatureLink}
                    readOnly
                  />
                  {showLinkActions && (
                    <button
                      type="button"
                      className="btn btn-outline-primary rg-signature-panel__copy-link"
                      onClick={onCopyLink}
                    >
                      <i className="bi bi-clipboard" aria-hidden="true"></i>
                      Copiar
                    </button>
                  )}
                </div>
              </div>

              {showLinkActions && (
                <div className="rg-signature-panel__actions">
                  {shareSupported && (
                    <div className="rg-signature-panel__primary-actions">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={onShareLink}
                        disabled={isSharingLink}
                        aria-busy={isSharingLink}
                      >
                        {isSharingLink && (
                          <span className="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>
                        )}
                        <i className="bi bi-share me-1" aria-hidden="true"></i>
                        {isSharingLink ? 'Compartiendo...' : 'Compartir enlace'}
                      </button>
                    </div>
                  )}
                  <div className="rg-signature-panel__secondary-actions">
                    <button
                      type="button"
                      className="btn btn-link rg-signature-panel__regenerate"
                      onClick={onRegenerateLink}
                      disabled={isGeneratingLink}
                      aria-busy={isRegeneratingLink}
                    >
                      {regenerateButtonLabel}
                    </button>
                  </div>
                </div>
              )}

              {isRegeneratingLink && (
                <p className="rg-signature-panel__action-feedback" role="status" aria-live="polite">
                  Generando un enlace nuevo. El enlace actual sigue disponible mientras tanto.
                </p>
              )}
            </>
          )}
        </div>

        {showLinkContent && (
          <div className="rg-signature-panel__digital-qr" aria-label="Código QR de firma">
            <div className="rg-signature-panel__qr">
              <canvas ref={qrCanvasRef} />
            </div>
          </div>
        )}
      </div>

      <div className="rg-signature-panel__action-live-region" aria-live="polite" aria-atomic="true">
        {signatureActionError ? (
          <div className="alert alert-warning mb-0" role="alert">
            {signatureActionError}
          </div>
        ) : signatureActionMessage ? (
          <p className="rg-signature-panel__action-feedback" role="status">
            {signatureActionMessage}
          </p>
        ) : null}
      </div>

      {signatureError && (
        <div className="alert alert-danger mb-0" role="alert">
          {signatureError}
        </div>
      )}
    </section>
  );
};

export { SIGNATURE_STATUS, canUseNativeShare, isValidSignatureUrl };
export default MobileSignatureLinkPanel;
